import {api, RequestParam} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {FieldObserver} from "@/shared/ui/fieldObserver";
import {FormUtil} from "@/shared/forms/formUtil";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import * as Constants from "@/shared/constants";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {copyOfObject, serializeObjectToArray} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDuration, formatTime, getISOTime, parseDuration, parseISOTime, parseTimeInput} from "@/shared/utils/datefunctions";
import {ErrorResponse, OvertimeRule, OvertimeRuleCategory, OvertimeRuleType} from "@/shared/timr";

export enum BaseRuleType {
	DAILY = "DAILY",
	WEEKLY = "WEEKLY"
}

export class OvertimeRulesAdministrationPage {

	private readonly overtimeRulesTable: OvertimeRulesTable;
	private readonly overtimeRulesDialogHandler: OvertimeRulesDialogHandler;

	constructor() {
		this.overtimeRulesDialogHandler = new OvertimeRulesDialogHandler();

		$("#overtimeRuleAddButton").click(() => {
			this.overtimeRulesDialogHandler.resetOvertimeRule();
			this.overtimeRulesDialogHandler.openDialog();
		});

		$("#overtimeRulesFilterInactive").change(() => {
			this.overtimeRulesTable.filter(this.buildTableFilterData());
		});

		this.overtimeRulesTable = new OvertimeRulesTable({
			element: $("#overtimeRulesTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			initialFilter: this.buildTableFilterData()
		});

		$(this.overtimeRulesDialogHandler)
			.on(OvertimeRulesDialogHandler.EVENT_OVERTIME_RULE_UPDATED, () => {
				this.overtimeRulesTable.filter(this.buildTableFilterData());
			})
			.on(OvertimeRulesDialogHandler.EVENT_OVERTIME_RULE_DELETE, (event, overtimeRule: OvertimeRule) => {
				this.deleteOvertimeRule(overtimeRule);
			});
		$(this.overtimeRulesTable)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + OvertimeRulesTable.EVENT_OVERTIME_RULE_EDIT, (event, overtimeRule: OvertimeRule) => {
				if (overtimeRule) {
					this.overtimeRulesDialogHandler.setOvertimeRule(overtimeRule);
				}
				this.overtimeRulesDialogHandler.openDialog();
			})
			.on(OvertimeRulesTable.EVENT_OVERTIME_RULE_ACTIVATE, (event, overtimeRule: OvertimeRule) => {
				this.activateOvertimeRule(overtimeRule);
			})
			.on(OvertimeRulesTable.EVENT_OVERTIME_RULE_DEACTIVATE, (event, overtimeRule: OvertimeRule) => {
				this.deactivateOvertimeRule(overtimeRule);
			})
			.on(OvertimeRulesTable.EVENT_OVERTIME_RULE_DELETE, (event, overtimeRule: OvertimeRule) => {
				this.deleteOvertimeRule(overtimeRule);
			});
	}

	private activateOvertimeRule(overtimeRule: OvertimeRule) {
		api("admin/activateOvertimeRule.json")
			.post({
				id: overtimeRule.id
			})
			.done(() => {
				this.overtimeRulesDialogHandler.close();
				this.overtimeRulesTable.reload();
			});
	}

	private deactivateOvertimeRule(overtimeRule: OvertimeRule) {
		api("admin/deactivateOvertimeRule.json")
			.post({
				id: overtimeRule.id
			})
			.done(() => {
				this.overtimeRulesDialogHandler.close();
				this.overtimeRulesTable.reload();
			});
	}

	private deleteOvertimeRule(overtimeRule: OvertimeRule) {
		BootstrapModal.confirm({
			title: "overtimeRule.context.delete",
			message: messages.withArguments("overtimeRule.confirm.delete", overtimeRule.name),
			yesLabel: "confirmation.delete",
			yesAction: (modal) => {
				modal.close();
				api("admin/deleteOvertimeRule.json")
					.post({
						id: overtimeRule.id
					})
					.done(status => {
						this.overtimeRulesDialogHandler.close();
						this.overtimeRulesTable.reload();
					});
			},
			noLabel: "confirmation.cancel"
		});
	}

	private buildTableFilterData() {
		return [
			{
				name: "active",
				value: $("#overtimeRulesFilterInactive").val() as string
			}
		];
	}
}

export class OvertimeRulesDialogHandler {

	public static EVENT_OVERTIME_RULE_DELETE = "eventOvertimeRuleDelete";
	public static EVENT_OVERTIME_RULE_UPDATED = "eventOvertimeRuleUpdated";

	private readonly overtimeRuleModal: BootstrapModal.Modal;
	private readonly observer: FieldObserver<OvertimeRule>;

	private overtimeRule: OvertimeRule;
	private baseRuleTypeControl: ButtonRadioGroup<BaseRuleType>;
	private originalDurationOver: number;
	private timeRangeChanged = false;

	constructor() {
		this.overtimeRuleModal = new BootstrapModal.Modal({
			title: "overtimeRule.header.create",
			message: $("#overtimeRuleDialog"),
			size: "modal-lg",
			destroyOnClose: false,
			buttons: [
				{
					text: "dialog.save",
					css: "saveButton btn-primary",
					action: () => {
						this.checkAndSaveOvertimeRule();
					}
				},
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default",
					action: () => {
						this.close();
						this.resetOvertimeRule();
					},
				},
				{
					text: "dialog.delete",
					css: "btn-outline-danger",
					left: true,
					action: () => {
						$(this).triggerHandler(OvertimeRulesDialogHandler.EVENT_OVERTIME_RULE_DELETE, this.overtimeRule);
					}
				}
			],
			onOpen: () => {
				$("#overtimeRuleName").focus();
			},
			onClose: () => {
				this.resetOvertimeRule();
			}
		});

		this.observer = new FieldObserver<OvertimeRule>();
		this.observer.onFieldValueChange((event, data) => {
			if (data.field === "initiated") {
				this.enableRuleTypeByInitiated();
			}
			this.updateFieldValues();
		});
		this.initListener();
		this.initBaseRuleTypeModeControl();

		$("#overtimeRuleName").change(() => this.updateTitle());
	}

	public resetOvertimeRule() {
		this.setOvertimeRule({
			id: null,
			version: 0,
			name: "",
			shortName: "",
			ruleCategory: "OVERTIME",
			ruleType: "WHOLE_DAY",
			percentage: 0,
			monday: false,
			tuesday: false,
			wednesday: false,
			thursday: false,
			friday: false,
			saturday: false,
			sunday: false,
			holiday: false,
			durationOver: null,
			active: true,
			initiated: false
		});
		$(`input[type="checkbox"]`).removeProp("checked");
		$("#dailyDurationOverValue").val("");
		$("#dailyTargetDurationOverValue").val("");
		$("#dailyDurationOverAndTimeRangeValue").val("");
		$("#dailyTargetDurationOverAndTimeRangeValue").val("");
		$("#weeklyDurationOverValue").val("");
		$("#weeklyTargetDurationOverValue").val("");
		this.timeRangeChanged = false;
	}

	public setOvertimeRule(overtimeRule: OvertimeRule) {
		this.overtimeRule = copyOfObject(overtimeRule);
		this.observer.setModel(this.overtimeRule);
		this.originalDurationOver = this.overtimeRule.durationOver;

		this.updateBaseRuleType();
		this.enableRuleTypeByInitiated();
		this.updateTitle();
		this.updateFieldValues();
		this.setTimeFromTo();
		this.setDurationOver();
		this.setPercentage();
		this.updateDailyDurationOverZeroRadio();
		this.updateWeeklyDurationOverZeroRadio();
		this.disableAllRadioChildInput();

		FormUtil.removeAllFeedback($("#overtimeRuleForm"));
	}

	public openDialog() {
		this.overtimeRuleModal.show();
	}

	public close() {
		this.overtimeRuleModal.close();
	}

	private getBaseRuleType(): BaseRuleType {
		return this.isDailyRuleType(this.overtimeRule.ruleType) ? BaseRuleType.DAILY : BaseRuleType.WEEKLY;
	}

	private updateBaseRuleType() {
		this.baseRuleTypeControl.setValue(this.getBaseRuleType());
	}

	private enableRuleTypeByInitiated() {
		const disabledRoleTypesForInitiated = ["WHOLE_DAY", "TIME_RANGE"];
		if (this.overtimeRule.initiated) {
			// Whole day and time range are not allowed for initiated overtimes, so we set the rule type to daily duration over
			if (disabledRoleTypesForInitiated.indexOf(this.overtimeRule.ruleType) !== -1) {
				this.setRuleType("DAILY_DURATION_OVER");
			}
		}
		disabledRoleTypesForInitiated.forEach(ruleType => {
			$(`input[name=ruleType][value=${ruleType}]`).prop("disabled", this.overtimeRule.initiated);
		});
	}

	private setRuleType(ruleType: OvertimeRuleType) {
		this.overtimeRule.ruleType = ruleType;
		this.setRuleTypeRadio();
		this.updateRadioChildValues();
	}

	private initBaseRuleTypeModeControl() {
		this.baseRuleTypeControl = ButtonRadioGroup.typed({
			value: BaseRuleType.DAILY,
			button: document.getElementById("overtimeRuleBaseRuleTypeDaily")
		}, {
			value: BaseRuleType.WEEKLY,
			button: document.getElementById("overtimeRuleBaseRuleTypeWeekly")
		}).onValueChanged(mode => {
			this.toggleBaseRuleTypeFields(mode);
		});
		this.baseRuleTypeControl.setValue(BaseRuleType.DAILY);
	}

	/**
	 * Shows/Hides the different rule type groups, there is a group for daily and weekly based rule types
	 * These groups contains radio options for each rule type (+ an extra special rule type with a "hidden" 0 for WEEKLY_DURATION_OVER)
	 * @param baseRuleType
	 * @private
	 */
	private toggleBaseRuleTypeFields(baseRuleType: BaseRuleType) {
		const isDaily = baseRuleType === BaseRuleType.DAILY;
		$("#overtimeRuleBaseRuleTypeDailyGroup").toggle(isDaily)
			.find(`input[type=radio]:not([disabled]):first`)
			.prop("checked", isDaily);
		$("#overtimeRuleBaseRuleTypeWeeklyGroup").toggle(!isDaily)
			.find(`input[type=radio]:not([disabled]):first`)
			.prop("checked", !isDaily);
		// Only change the rule type if the current rule type is not allowed for the selected base rule type
		if (this.overtimeRule?.ruleType && !this.isDailyRuleType(this.overtimeRule?.ruleType) && isDaily) {
			this.overtimeRule.ruleType = this.overtimeRule.initiated ? "DAILY_DURATION_OVER" : "WHOLE_DAY";
		}
		if (this.overtimeRule?.ruleType && this.isDailyRuleType(this.overtimeRule?.ruleType) && !isDaily) {
			this.overtimeRule.ruleType = $("#overtimeRuleBaseRuleTypeWeeklyGroup").find(`input[type=radio]:not([disabled]):first`).val() as OvertimeRuleType;
		}
		this.disableAllRadioChildInput();
	}

	private initListener() {
		this.observer.observeInput("#overtimeRuleName", "name");
		this.observer.observeInput("#overtimeRuleShortName", "shortName");
		this.observer.observeInput("input[name=ruleCategory]", "ruleCategory", radio => $(radio).filter(":checked").val() as OvertimeRuleCategory);
		this.observer.observeInput("#overtimeRulePercentage", "percentage", input => Number($(input).val()) / 100);
		this.observer.observeInput("#overtimeRuleMonday", "monday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleTuesday", "tuesday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleWednesday", "wednesday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleThursday", "thursday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleFriday", "friday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleSaturday", "saturday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleSunday", "sunday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleHoliday", "holiday", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleInitiatedDaily", "initiated", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("#overtimeRuleInitiatedWeekly", "initiated", checkbox => $(checkbox).prop("checked") as boolean);
		this.observer.observeInput("input[name=ruleType]", "ruleType", radio => $(radio).filter(":checked").val() as OvertimeRuleType);
		$("#timeRangeFrom").change(this.updateTimeFrom);
		$("#timeRangeTo").change(this.updateTimeTo);
		$("#dailyDurationOverAndTimeRangeFrom").change(this.updateTimeFrom);
		$("#dailyDurationOverAndTimeRangeTo").change(this.updateTimeTo);
		$("#dailyDurationOverAndTimeRangeValue").change(this.updateDurationOver);
		$("#dailyDurationOverValue").change(this.updateDurationOver);
		$("#dailyTargetDurationOverAndTimeRangeFrom").change(this.updateTimeFrom);
		$("#dailyTargetDurationOverAndTimeRangeTo").change(this.updateTimeTo);
		$("#dailyTargetDurationOverAndTimeRangeValue").change(this.updateDurationOver);
		$("#dailyTargetDurationOverValue").change(this.updateDurationOver);
		$("#weeklyDurationOverValue").change(this.updateDurationOver);
		$("#weeklyTargetDurationOverValue").change(this.updateDurationOver);
		$("input[name=ruleType]").change(this.updateRadioChildValues);
	}

	private updateFieldValues() {
		const editCategoryMode = this.overtimeRule.id != null;
		this.overtimeRuleModal.toggleButton("btn-outline-danger", editCategoryMode);
		$("#overtimeRuleActiveFormGroup").toggle(editCategoryMode);

		$("#overtimeRuleName").val(this.overtimeRule.name);
		$("#overtimeRuleShortName").val(this.overtimeRule.shortName);
		$(`input[name=ruleCategory][value=${this.overtimeRule.ruleCategory}]`).prop("checked", true);
		$("#overtimeRuleCategory").val(this.overtimeRule.ruleCategory);
		$("#overtimeRuleMonday").prop("checked", this.overtimeRule.monday);
		$("#overtimeRuleTuesday").prop("checked", this.overtimeRule.tuesday);
		$("#overtimeRuleWednesday").prop("checked", this.overtimeRule.wednesday);
		$("#overtimeRuleThursday").prop("checked", this.overtimeRule.thursday);
		$("#overtimeRuleFriday").prop("checked", this.overtimeRule.friday);
		$("#overtimeRuleSaturday").prop("checked", this.overtimeRule.saturday);
		$("#overtimeRuleSunday").prop("checked", this.overtimeRule.sunday);
		$("#overtimeRuleHoliday").prop("checked", this.overtimeRule.holiday);
		$("#overtimeRuleInitiatedDaily").prop("checked", this.overtimeRule.initiated);
		$("#overtimeRuleInitiatedWeekly").prop("checked", this.overtimeRule.initiated);
		this.setRuleTypeRadio();
	}

	private checkAndSaveOvertimeRule = () => {
		const button = this.overtimeRuleModal.getButtons("btn-primary");
		const form = $("#overtimeRuleForm");
		Button.setLoading(button);

		this.prepareValuesForSave();
		const overtimeRulesData = serializeObjectToArray({...this.overtimeRule});

		if (this.overtimeRule.id && (this.observer.hasChanged() || (this.overtimeRule.durationOver !== this.originalDurationOver) || this.timeRangeChanged)) {
			api("admin/checkIfOvertimeRuleHasUnbalancedWorkingTimes.json")
				.get(overtimeRulesData)
				.then(data => {
					FormUtil.removeAllFeedback(form, false);
					if (data.foundWorkingTimes > 0) {
						BootstrapModal.confirm({
							title: "dialog.update.overtimeRule.title",
							message: "dialog.update.overtimeRule.message",
							escapeText: false,
							yesLabel: "dialog.update.overtimeRule.yes",
							yesAction: (confirmModal) => {
								this.saveOvertimeRule(overtimeRulesData, [confirmModal, this.overtimeRuleModal]);
							},
							noLabel: "dialog.update.overtimeRule.no",
							noAction: (modal) => {
								modal.close();
								Button.resetLoading(button);
							}
						});
					} else {
						this.saveOvertimeRule(overtimeRulesData, [this.overtimeRuleModal]);
					}
				})
				.fail(this.markOvertimeRuleDialogErrors)
				.always(() => {
					Button.resetLoading(this.overtimeRuleModal.getButtons("btn-primary"));
				});
		} else {
			this.saveOvertimeRule(overtimeRulesData, [this.overtimeRuleModal]);
		}
	}

	private saveOvertimeRule(overtimeRulesData: RequestParam[], modals: BootstrapModal.Modal[]) {
		FormUtil.removeAllFeedback($("#overtimeRuleForm"));

		api("admin/saveOvertimeRule.json")
			.post(overtimeRulesData)
			.then(status => {
				SecurityStatus.update(status.securityStatus);

				modals.forEach(modal => modal.close());
				$(this).triggerHandler(OvertimeRulesDialogHandler.EVENT_OVERTIME_RULE_UPDATED);
			})
			.fail(this.markOvertimeRuleDialogErrors)
			.always(() => {
				modals.forEach(modal => Button.resetLoading(modal.getButtons("btn-primary")));
			});
	}

	private updateTitle() {
		let message = messages[this.overtimeRule.id == null ? "overtimeRule.header.create" : "overtimeRule.header.edit"];
		if (this.overtimeRule.name) {
			message += ": " + this.overtimeRule.name;
		}
		this.overtimeRuleModal.setTitle(message, true);
	}

	/**
	 * Updates the state of the value of the dailyDurationOverZero ruleType
	 * There is a special case for a duplicate ruleType radio option because it is an option with a "hidden" 0
	 *
	 * @private
	 */
	private updateDailyDurationOverZeroRadio() {
		// DEVNOTE: !this.overtimeRule.durationOver is also true if the durationOver is 0
		if (this.overtimeRule.ruleType === "DAILY_TARGET_WORK_DURATION_OVER") {
			if (this.overtimeRule.durationOver) {
				$("#dailyTargetDurationOver").prop("checked", true);
				Input.enable($("#dailyTargetDurationOverValue"));
				return;
			} else {
				$("#dailyTargetDurationOverZero").prop("checked", true);
				Input.disable($("#dailyTargetDurationOverValue"));
				return;
			}
		}
	}

	/**
	 * Updates the state of the value of the weeklyDurationOverZero ruleType
	 * There is a special case for a duplicate ruleType radio option because it is an option with a "hidden" 0
	 *
	 * @private
	 */
	private updateWeeklyDurationOverZeroRadio() {
		// DEVNOTE: !this.overtimeRule.durationOver is also true if the durationOver is 0
		if (this.overtimeRule.ruleType === "WEEKLY_TARGET_WORK_DURATION_OVER") {
			if (this.overtimeRule.durationOver) {
				$("#weeklyTargetDurationOver").prop("checked", true);
				Input.enable($("#weeklyTargetDurationOverValue"));
				return;
			} else {
				$("#weeklyTargetDurationOverZero").prop("checked", true);
				Input.disable($("#weeklyTargetDurationOverValue"));
				return;
			}
		}
	}

	private setRuleTypeRadio() {
		// Special case for a duplicate ruleType radio option
		if (this.overtimeRule.ruleType !== "DAILY_TARGET_WORK_DURATION_OVER" && this.overtimeRule.ruleType !== "WEEKLY_TARGET_WORK_DURATION_OVER") {
			$(`input[name=ruleType][value=${this.overtimeRule.ruleType}]`).prop("checked", true);
		}
	}

	/**
	 * Disables all child radio value inputs of the ruleType radio except the currently marked one
	 * @private
	 */
	private disableAllRadioChildInput() {
		$(".overtimeRuleBaseRuleTypeGroup input[type=text]").prop("disabled", true);
		$(`input[name=ruleType]:checked`)
			.closest(".radio-inline")
			.find("input[type=text]")
			.prop("disabled", false);
	}

	/**
	 * Prepares the necessary values for the respective rule before saving the overtime rule
	 * There is a special case for a duplicate ruleType radio option because it is an option with a "hidden" 0
	 *
	 * @private
	 */
	private prepareValuesForSave() {
		if ($("#weeklyTargetDurationOverZero").is(":checked")) {
			this.overtimeRule.ruleType = "WEEKLY_TARGET_WORK_DURATION_OVER";
			this.overtimeRule.durationOver = 0;
			this.overtimeRule.timeTo = null;
			this.overtimeRule.timeFrom = null;
			return;
		} else if ($("#dailyTargetDurationOverZero").is(":checked")) {
			this.overtimeRule.ruleType = "DAILY_TARGET_WORK_DURATION_OVER";
			this.overtimeRule.durationOver = 0;
			this.overtimeRule.timeTo = null;
			this.overtimeRule.timeFrom = null;
			return;
		}

		switch (this.overtimeRule.ruleType) {
			case "WHOLE_DAY":
				this.overtimeRule.durationOver = null;
				this.overtimeRule.timeTo = null;
				this.overtimeRule.timeFrom = null;
				break;
			case "TIME_RANGE":
				this.overtimeRule.timeFrom = $("#timeRangeFrom").val() !== "" ?
					FieldObserver.getTimeField("#timeRangeFrom") : null;
				this.overtimeRule.timeTo = $("#timeRangeTo").val() !== "" ?
					FieldObserver.getTimeField("#timeRangeTo") : null;
				this.overtimeRule.durationOver = null;
				break;
			case "DAILY_TARGET_WORK_DURATION_OVER":
				this.overtimeRule.timeTo = null;
				this.overtimeRule.timeFrom = null;
				this.overtimeRule.durationOver = $("#dailyTargetDurationOverValue").val() !== "" ?
					FieldObserver.getDurationFromField("#dailyTargetDurationOverValue") : null;
				break;
			case "DAILY_TARGET_DURATION_OVER_AND_TIME_RANGE":
				this.overtimeRule.timeFrom = $("#dailyTargetDurationOverAndTimeRangeFrom").val() !== "" ?
					FieldObserver.getTimeField("#dailyTargetDurationOverAndTimeRangeFrom") : null;
				this.overtimeRule.timeTo = $("#dailyTargetDurationOverAndTimeRangeTo").val() !== "" ?
					FieldObserver.getTimeField("#dailyTargetDurationOverAndTimeRangeTo") : null;
				this.overtimeRule.durationOver = $("#dailyTargetDurationOverAndTimeRangeValue").val() !== "" ?
					FieldObserver.getDurationFromField("#dailyTargetDurationOverAndTimeRangeValue") : null;
				break;
			case "DAILY_DURATION_OVER":
				this.overtimeRule.timeTo = null;
				this.overtimeRule.timeFrom = null;
				this.overtimeRule.durationOver = $("#dailyDurationOverValue").val() !== "" ?
					FieldObserver.getDurationFromField("#dailyDurationOverValue") : null;
				break;
			case "DAILY_DURATION_OVER_AND_TIME_RANGE":
				this.overtimeRule.timeFrom = $("#dailyDurationOverAndTimeRangeFrom").val() !== "" ?
					FieldObserver.getTimeField("#dailyDurationOverAndTimeRangeFrom") : null;
				this.overtimeRule.timeTo = $("#dailyDurationOverAndTimeRangeTo").val() !== "" ?
					FieldObserver.getTimeField("#dailyDurationOverAndTimeRangeTo") : null;
				this.overtimeRule.durationOver = $("#dailyDurationOverAndTimeRangeValue").val() !== "" ?
					FieldObserver.getDurationFromField("#dailyDurationOverAndTimeRangeValue") : null;
				break;
			case "WEEKLY_DURATION_OVER":
				this.overtimeRule.timeTo = null;
				this.overtimeRule.timeFrom = null;
				this.overtimeRule.durationOver = $("#weeklyDurationOverValue").val() !== "" ?
					FieldObserver.getDurationFromField("#weeklyDurationOverValue") : null;
				break;
			case "WEEKLY_TARGET_WORK_DURATION_OVER":
				this.overtimeRule.timeTo = null;
				this.overtimeRule.timeFrom = null;
				this.overtimeRule.durationOver = $("#weeklyTargetDurationOverValue").val() !== "" ?
					FieldObserver.getDurationFromField("#weeklyTargetDurationOverValue") : null;
				break;
		}
	}

	private setPercentage() {
		$("#overtimeRulePercentage").val(Math.round(this.overtimeRule.percentage * 100));
	}

	private setTimeFromTo() {
		$("#timeRangeFrom").val("");
		$("#timeRangeTo").val("");
		$("#dailyDurationOverAndTimeRangeFrom").val("");
		$("#dailyDurationOverAndTimeRangeTo").val("");
		$("#dailyTargetDurationOverAndTimeRangeFrom").val("");
		$("#dailyTargetDurationOverAndTimeRangeTo").val("");
		switch (this.overtimeRule.ruleType) {
			case "TIME_RANGE":
				$("#timeRangeFrom")
					.val(formatTime(parseISOTime(this.overtimeRule.timeFrom)))
					.prop("disabled", false);
				$("#timeRangeTo")
					.val(formatTime(parseISOTime(this.overtimeRule.timeTo)))
					.prop("disabled", false);
				break;
			case "DAILY_DURATION_OVER_AND_TIME_RANGE":
				$("#dailyDurationOverAndTimeRangeFrom")
					.val(formatTime(parseISOTime(this.overtimeRule.timeFrom)))
					.prop("disabled", false);
				$("#dailyDurationOverAndTimeRangeTo")
					.val(formatTime(parseISOTime(this.overtimeRule.timeTo)))
					.prop("disabled", false);
				break;
			case "DAILY_TARGET_DURATION_OVER_AND_TIME_RANGE":
				$("#dailyTargetDurationOverAndTimeRangeFrom")
					.val(formatTime(parseISOTime(this.overtimeRule.timeFrom)))
					.prop("disabled", false);
				$("#dailyTargetDurationOverAndTimeRangeTo")
					.val(formatTime(parseISOTime(this.overtimeRule.timeTo)))
					.prop("disabled", false);
				break;
		}
	}

	/**
	 * Sets the value of the input field of the selected ruleType radio option if the ruleType requires this field.
	 * There is a special case for a duplicate ruleType radio option, since it is an option with a "hidden" 0
	 * No value needs to be set for this option
	 * @private
	 */
	private setDurationOver() {
		const dailyDurationOverValue = $("#dailyDurationOverValue").val("");
		const dailyTargetDurationOverValue = $("#dailyTargetDurationOverValue").val("");
		const dailyDurationOverAndTimeRangeValue = $("#dailyDurationOverAndTimeRangeValue").val("");
		const dailyTargetDurationOverAndTimeRangeValue = $("#dailyTargetDurationOverAndTimeRangeValue").val("");
		const weeklyDurationOverValue = $("#weeklyDurationOverValue").val("");
		const weeklyTargetDurationOverValue = $("#weeklyTargetDurationOverValue").val("");

		if ((this.overtimeRule.ruleType === "WEEKLY_TARGET_WORK_DURATION_OVER" || this.overtimeRule.ruleType === "DAILY_TARGET_WORK_DURATION_OVER")
			&& this.overtimeRule.durationOver === 0) {
			return;
		}
		switch (this.overtimeRule.ruleType) {
			case "DAILY_TARGET_WORK_DURATION_OVER":
				dailyTargetDurationOverValue
					.val(formatDuration(this.overtimeRule.durationOver))
					.prop("disabled", false);
				break;
			case "DAILY_DURATION_OVER":
				dailyDurationOverValue
					.val(formatDuration(this.overtimeRule.durationOver))
					.prop("disabled", false);
				break;
			case "DAILY_DURATION_OVER_AND_TIME_RANGE":
				dailyDurationOverAndTimeRangeValue
					.val(formatDuration(this.overtimeRule.durationOver))
					.prop("disabled", false);
				break;
			case "DAILY_TARGET_DURATION_OVER_AND_TIME_RANGE":
				dailyTargetDurationOverAndTimeRangeValue
					.val(formatDuration(this.overtimeRule.durationOver))
					.prop("disabled", false);
				break;
			case "WEEKLY_DURATION_OVER":
				weeklyDurationOverValue
					.val(formatDuration(this.overtimeRule.durationOver))
					.prop("disabled", false);
				break;
			case "WEEKLY_TARGET_WORK_DURATION_OVER":
				weeklyTargetDurationOverValue
					.val(formatDuration(this.overtimeRule.durationOver))
					.prop("disabled", false);
				break;
		}
	}

	/**
	 * The child inputs of the current selected option will be enabled, all others get disabled
	 * The first child input will be focused
	 */
	private updateRadioChildValues = () => {
		$(".overtimeRuleBaseRuleTypeGroup input[type=text]").prop("disabled", true);
		$("input[name=ruleType]:checked").closest(".radio-inline")
			.find("input[type=text]")
			.prop("disabled", false)
			.first()
			.focus();
	}

	private updateDurationOver = (e: JQuery.TriggeredEvent) => {
		const durationOver = parseDuration($(e.target).val() as string, true) * 60000;
		$(e.target).val(formatDuration(durationOver));
		if (this.overtimeRule.durationOver !== durationOver) {
			this.overtimeRule.durationOver = durationOver;
		}
	}

	private updateTimeFrom = (e: JQuery.TriggeredEvent) => {
		const timeFrom = parseTimeInput($(e.target).val() as string);
		$(e.target).val(formatTime(timeFrom));
		if (this.overtimeRule.timeFrom !== getISOTime(timeFrom)) {
			this.timeRangeChanged = true;
		}
	}

	private updateTimeTo = (e: JQuery.TriggeredEvent) => {
		const timeTo = parseTimeInput($(e.target).val() as string);
		$(e.target).val(formatTime(timeTo));
		if (this.overtimeRule.timeTo !== getISOTime(timeTo)) {
			this.timeRangeChanged = true;
		}
	}

	/**
	 * Marks the field Error after when errors occur
	 * The different values of durationOver for the individual options of radio ruleType require individual handling of the field error marker
	 * @private
	 */
	private markOvertimeRuleDialogErrors = (response: ErrorResponse) => {
		if (response.responseJSON?.fieldErrors) {
			const form = $("#overtimeRuleForm");
			FormUtil.removeAllFeedback(form, false);

			response.responseJSON.fieldErrors.forEach(fieldError => {
				const field = fieldError.field;
				let input: JQuery;
				if (field === "durationOver" || field === "timeFrom" || field === "timeTo") {
					input = $(`input[name=ruleType]:checked`)
						.closest(".radio-inline")
						.find(`input[name="${field}"]`);
				} else {
					input = form.find(`input[name="${field}"], select[name="${field}"], textarea[name="${field}"]`);
				}
				Input.addFeedback(input, "has-error", fieldError.message);
			});
		}
	}

	private isDailyRuleType(ruleType: OvertimeRuleType): boolean {
		return ruleType === "WHOLE_DAY" || ruleType === "TIME_RANGE" ||
			ruleType === "DAILY_TARGET_WORK_DURATION_OVER" || ruleType === "DAILY_DURATION_OVER" ||
			ruleType === "DAILY_DURATION_OVER_AND_TIME_RANGE" || ruleType === "DAILY_TARGET_DURATION_OVER_AND_TIME_RANGE";
	}
}

export class OvertimeRulesTable extends BasicDataTable<OvertimeRule> {

	public static EVENT_OVERTIME_RULE_EDIT = "eventOvertimeRuleEdit";
	public static EVENT_OVERTIME_RULE_DELETE = "eventOvertimeRuleDelete";
	public static EVENT_OVERTIME_RULE_ACTIVATE = "eventOvertimeRuleActivate";
	public static EVENT_OVERTIME_RULE_DEACTIVATE = "eventOvertimeRuleDeactivate";

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/overtimeRules.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			rowCallback: (row, data, index) => {
				const overtimeRule = data as OvertimeRule;
				if (!overtimeRule.active) {
					$(row).addClass("archived");
				}
			},
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<OvertimeRule>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "shortName", orderable: true, render: BasicDataTable.renderText},
			{data: "percentage", orderable: true, render: this.renderPercentage},
			{
				data: "active",
				orderable: false,
				render: this.renderActive,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	private renderPercentage(this: void, data: number | null, type: string, recording: OvertimeRule, meta: DataTables.CellMetaSettings): string {
		return data != null ? Math.round(data * 100) + "%" : "";
	}

	private renderActive(this: void, data: boolean, type: string, recording: OvertimeRule, meta: DataTables.CellMetaSettings): string {
		return data ? "" : `<i class="fal fa-archive" title="${messages["overtimeRule.active"]}"></i>`;
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "overtimeRule.context.edit",
				action: overtimeRule => {
					$(this).triggerHandler(OvertimeRulesTable.EVENT_OVERTIME_RULE_EDIT, overtimeRule);
				}
			})
			.addMenuEntry({
				label: "overtimeRule.context.deactivate",
				action: overtimeRule => {
					$(this).triggerHandler(OvertimeRulesTable.EVENT_OVERTIME_RULE_DEACTIVATE, overtimeRule);
				},
				show: overtimeRule => overtimeRule.active
			})
			.addMenuEntry({
				label: "overtimeRule.context.activate",
				action: overtimeRule => {
					$(this).triggerHandler(OvertimeRulesTable.EVENT_OVERTIME_RULE_ACTIVATE, overtimeRule);
				},
				show: overtimeRule => !overtimeRule.active
			})
			.addMenuEntry({
				label: "overtimeRule.context.delete",
				action: overtimeRule => {
					$(this).triggerHandler(OvertimeRulesTable.EVENT_OVERTIME_RULE_DELETE, overtimeRule);
				}
			});
	}
}
